/**
 * Filter Chips Component
 * Displays active filters as compact, removable chips
 */

import { X, Filter } from 'lucide-react';
import {
  FilterCondition,
  WidgetFilterConfig,
  getOperatorLabel,
  formatFilterValue,
} from '../../types/filters';

interface FilterChipsProps {
  filterConfig: WidgetFilterConfig;
  onRemoveCondition: (groupId: string, conditionId: string) => void;
  onClearAll: () => void;
  onOpenFilter: () => void;
  compact?: boolean;
}

export function FilterChips({
  filterConfig,
  onRemoveCondition,
  onClearAll,
  onOpenFilter,
  compact = false,
}: FilterChipsProps) {
  // Count total conditions
  const totalConditions = filterConfig.groups.reduce(
    (sum, group) => sum + group.conditions.length,
    0
  );

  if (!filterConfig.enabled || totalConditions === 0) {
    return null;
  }

  // Compact mode: just show count
  if (compact) {
    return (
      <button
        onClick={onOpenFilter}
        className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-[#45bdd6] bg-[#45bdd6]/10 rounded-full hover:bg-[#45bdd6]/20 transition-colors"
        title="Edit filters"
      >
        <Filter className="w-3 h-3" />
        {totalConditions}
      </button>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-xs text-gray-500 font-medium">Filters:</span>

      {filterConfig.groups.map((group, groupIndex) => (
        <div key={group.id} className="flex items-center gap-1">
          {groupIndex > 0 && (
            <span className="text-xs text-gray-400 mx-1">AND</span>
          )}

          {group.conditions.map((condition, conditionIndex) => (
            <div key={condition.id} className="flex items-center gap-1">
              {conditionIndex > 0 && (
                <span className="text-xs text-gray-400">
                  {group.logic === 'and' ? '&' : '|'}
                </span>
              )}
              <FilterChip
                condition={condition}
                onRemove={() => onRemoveCondition(group.id, condition.id)}
              />
            </div>
          ))}
        </div>
      ))}

      {totalConditions > 1 && (
        <button
          onClick={onClearAll}
          className="text-xs text-gray-400 hover:text-red-500 ml-1"
          title="Clear all filters"
        >
          Clear all
        </button>
      )}
    </div>
  );
}

interface FilterChipProps {
  condition: FilterCondition;
  onRemove: () => void;
}

function FilterChip({ condition, onRemove }: FilterChipProps) {
  const operatorLabel = getOperatorLabel(condition.operator, condition.fieldType);
  const valueDisplay = formatFilterValue(condition);

  // Format the field name nicely
  const fieldLabel = condition.field
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .trim();

  // Build display text
  let displayText = fieldLabel;

  // Add operator for certain cases
  if (condition.operator === 'contains') {
    displayText += ` ~ "${valueDisplay}"`;
  } else if (condition.operator === 'starts_with') {
    displayText += ` ^= "${valueDisplay}"`;
  } else if (condition.operator === 'ends_with') {
    displayText += ` $= "${valueDisplay}"`;
  } else if (condition.operator === 'is_empty') {
    displayText += ' = ∅';
  } else if (condition.operator === 'is_not_empty') {
    displayText += ' ≠ ∅';
  } else if (condition.operator === 'is_true') {
    displayText += ' = ✓';
  } else if (condition.operator === 'is_false') {
    displayText += ' = ✗';
  } else if (condition.operator === 'today') {
    displayText += ' = today';
  } else if (condition.operator === 'this_week') {
    displayText += ' = this week';
  } else if (condition.operator === 'this_month') {
    displayText += ' = this month';
  } else if (condition.operator === 'last_n_days') {
    displayText += ` > ${valueDisplay}`;
  } else if (condition.operator === 'between') {
    displayText += ` ${valueDisplay}`;
  } else if (valueDisplay) {
    // Standard operators
    const opSymbol = getOperatorSymbol(condition.operator);
    displayText += ` ${opSymbol} ${valueDisplay}`;
  }

  return (
    <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-[#674EA7] bg-[#674EA7]/10 rounded-full max-w-xs">
      <span className="truncate" title={displayText}>
        {displayText}
      </span>
      <button
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        className="p-0.5 hover:bg-[#674EA7]/20 rounded-full transition-colors"
        title="Remove filter"
      >
        <X className="w-3 h-3" />
      </button>
    </span>
  );
}

function getOperatorSymbol(operator: string): string {
  switch (operator) {
    case 'equals':
      return '=';
    case 'not_equals':
      return '≠';
    case 'greater_than':
      return '>';
    case 'less_than':
      return '<';
    case 'greater_or_equal':
      return '≥';
    case 'less_or_equal':
      return '≤';
    case 'before':
      return '<';
    case 'after':
      return '>';
    default:
      return '=';
  }
}

// Inline filter badge for widget header
interface FilterBadgeProps {
  count: number;
  onClick: () => void;
}

export function FilterBadge({ count, onClick }: FilterBadgeProps) {
  if (count === 0) return null;

  return (
    <button
      onClick={onClick}
      className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-[#45bdd6] bg-[#45bdd6]/10 rounded-full hover:bg-[#45bdd6]/20 transition-colors"
      title={`${count} active filters`}
    >
      <Filter className="w-3 h-3" />
      <span>{count}</span>
    </button>
  );
}

// Filter indicator for collapsed view
interface FilterIndicatorProps {
  filterConfig: WidgetFilterConfig;
  onClick: () => void;
}

export function FilterIndicator({ filterConfig, onClick }: FilterIndicatorProps) {
  const totalConditions = filterConfig.groups.reduce(
    (sum, group) => sum + group.conditions.length,
    0
  );

  if (!filterConfig.enabled || totalConditions === 0) {
    return (
      <button
        onClick={onClick}
        className="p-1.5 text-gray-400 hover:text-[#45bdd6] hover:bg-[#45bdd6]/10 rounded transition-colors"
        title="Add filter"
      >
        <Filter className="w-4 h-4" />
      </button>
    );
  }

  return (
    <button
      onClick={onClick}
      className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-white bg-[#45bdd6] rounded hover:bg-[#3aa8bf] transition-colors"
      title={`${totalConditions} active filters - Click to edit`}
    >
      <Filter className="w-3.5 h-3.5" />
      <span>{totalConditions}</span>
    </button>
  );
}
